import { AssetAllocation, SimulationParameters, SimulationResult, ChartDataPoint, WithdrawalDataPoint, AccountBalance, AccountType, AccountBreakdownDataPoint, SimulationResults } from '../types/AssetClasses';
import { getAssetClassById } from '../data/AssetClassData';
import { ScenarioDataMap } from '../data/ScenarioData';

export class MonteCarloSimulation {
  private parameters: SimulationParameters;
  private scenarioData?: ScenarioDataMap;

  constructor(parameters: SimulationParameters, options?: { scenarioData?: ScenarioDataMap }) {
    this.parameters = parameters;
    this.scenarioData = options?.scenarioData;
  }

  /**
   * Run a single simulation
   */
  private runSingleSimulation(scenarioRowIndex: number): {
    accountBalances: AccountBalance[][],
    totalValues: number[],
    withdrawals: number[]
  } {
    const accountResults: AccountBalance[][] = [];
    const totalResults: number[] = [];
    const withdrawalResults: number[] = [];

    // Initialize current account balances
    let currentAccounts = this.parameters.accountBalances.map(account => ({
      ...account,
      allocations: account.allocations.map(allocation => ({
        ...allocation,
        amount: (allocation.percentage / 100) * account.balance
      }))
    }));

    let currentWithdrawal = this.parameters.monthlySpendingTarget * 12;

    if (!this.scenarioData) {
      throw new Error('Scenario data is required but was not provided.');
    }

    const periods = Math.min(this.parameters.simulationPeriods, 35);

    for (let period = 0; period < periods; period++) {
      // Apply returns to each account separately
      currentAccounts = currentAccounts.map(account => {
        const newAllocations = account.allocations.map(allocation => {
          const assetClass = getAssetClassById(allocation.assetClassId);
          if (!assetClass) return allocation;

          let returnRate = 0;
          switch (assetClass.id) {
            case 'USEquity':
              returnRate = this.scenarioData!.usEquity[scenarioRowIndex][period];
              break;
            case 'InternationalEquity':
              returnRate = this.scenarioData!.internationalEquity[scenarioRowIndex][period];
              break;
            case 'AggregateBonds':
              returnRate = this.scenarioData!.bonds[scenarioRowIndex][period];
              break;
            case 'Cash':
              returnRate = this.scenarioData!.cash[scenarioRowIndex][period];
              break;
            default:
              returnRate = 0;
          }

          return {
            ...allocation,
            amount: allocation.amount * (1 + returnRate)
          };
        });

        // Calculate new account balance
        const newBalance = newAllocations.reduce((sum, alloc) => sum + alloc.amount, 0);

        // Rebalance to original allocation percentages
        if (newBalance > 0) {
          const totalPercentage = account.allocations.reduce((sum, alloc) => sum + alloc.percentage, 0);
          const rebalancedAllocations = account.allocations.map(allocation => ({
            ...allocation,
            amount: (allocation.percentage / totalPercentage) * newBalance
          }));

          return {
            ...account,
            balance: newBalance,
            allocations: rebalancedAllocations
          };
        } else {
          return {
            ...account,
            balance: 0,
            allocations: account.allocations.map(alloc => ({ ...alloc, amount: 0 }))
          };
        }
      });

      // Calculate total portfolio value (for reference, not used in this iteration)
      // const totalValue = currentAccounts.reduce((sum, account) => sum + account.balance, 0);

      // Scenario-based inflation of withdrawal amount
      const inflationRate = this.scenarioData.inflation[scenarioRowIndex][period];
      currentWithdrawal *= (1 + inflationRate);

      // Store withdrawal amount for this period
      withdrawalResults.push(currentWithdrawal);

      // Calculate Social Security benefit for this period
      // The COLA adjustment being directly tied to the inflation rate is a simplification.
      const socialSecurityBenefit = this.parameters.socialSecurityMonthlyBenefit * 12 * (1 + inflationRate);

      // Apply withdrawal with priority order
      let remainingWithdrawal = Math.max(0, currentWithdrawal - socialSecurityBenefit);

      // 1. Cash Savings first
      const cashAccount = currentAccounts.find(acc => acc.accountType === AccountType.CASH_SAVINGS);
      if (cashAccount && remainingWithdrawal > 0) {
        const withdrawalFromCash = Math.min(remainingWithdrawal, cashAccount.balance);
        cashAccount.balance -= withdrawalFromCash;
        remainingWithdrawal -= withdrawalFromCash;

        // Update cash allocations
        cashAccount.allocations = cashAccount.allocations.map(alloc => ({
          ...alloc,
          amount: alloc.percentage / 100 * cashAccount.balance
        }));
      }

      // 2. Tax Deferred
      const taxDeferredAccount = currentAccounts.find(acc => acc.accountType === AccountType.TAX_DEFERRED);
      if (taxDeferredAccount && remainingWithdrawal > 0) {
        const withdrawalFromTaxDeferred = Math.min(remainingWithdrawal, taxDeferredAccount.balance);
        taxDeferredAccount.balance -= withdrawalFromTaxDeferred;
        remainingWithdrawal -= withdrawalFromTaxDeferred;

        // Rebalance tax deferred account
        if (taxDeferredAccount.balance > 0) {
          const totalPercentage = taxDeferredAccount.allocations.reduce((sum, alloc) => sum + alloc.percentage, 0);
          taxDeferredAccount.allocations = taxDeferredAccount.allocations.map(alloc => ({
            ...alloc,
            amount: (alloc.percentage / totalPercentage) * taxDeferredAccount.balance
          }));
        } else {
          taxDeferredAccount.allocations = taxDeferredAccount.allocations.map(alloc => ({ ...alloc, amount: 0 }));
        }
      }

      // 3. Taxable Investment
      const taxableAccount = currentAccounts.find(acc => acc.accountType === AccountType.TAXABLE_INVESTMENT);
      if (taxableAccount && remainingWithdrawal > 0) {
        const withdrawalFromTaxable = Math.min(remainingWithdrawal, taxableAccount.balance);
        taxableAccount.balance -= withdrawalFromTaxable;
        remainingWithdrawal -= withdrawalFromTaxable;

        // Rebalance taxable account
        if (taxableAccount.balance > 0) {
          const totalPercentage = taxableAccount.allocations.reduce((sum, alloc) => sum + alloc.percentage, 0);
          taxableAccount.allocations = taxableAccount.allocations.map(alloc => ({
            ...alloc,
            amount: (alloc.percentage / totalPercentage) * taxableAccount.balance
          }));
        } else {
          taxableAccount.allocations = taxableAccount.allocations.map(alloc => ({ ...alloc, amount: 0 }));
        }
      }

      // 4. Tax Free
      const taxFreeAccount = currentAccounts.find(acc => acc.accountType === AccountType.TAX_FREE);
      if (taxFreeAccount && remainingWithdrawal > 0) {
        const withdrawalFromTaxFree = Math.min(remainingWithdrawal, taxFreeAccount.balance);
        taxFreeAccount.balance -= withdrawalFromTaxFree;
        remainingWithdrawal -= withdrawalFromTaxFree;

        // Rebalance tax free account
        if (taxFreeAccount.balance > 0) {
          const totalPercentage = taxFreeAccount.allocations.reduce((sum, alloc) => sum + alloc.percentage, 0);
          taxFreeAccount.allocations = taxFreeAccount.allocations.map(alloc => ({
            ...alloc,
            amount: (alloc.percentage / totalPercentage) * taxFreeAccount.balance
          }));
        } else {
          taxFreeAccount.allocations = taxFreeAccount.allocations.map(alloc => ({ ...alloc, amount: 0 }));
        }
      }

      // Store results for this period
      accountResults.push([...currentAccounts]);
      totalResults.push(currentAccounts.reduce((sum, account) => sum + account.balance, 0));
    }

    return { accountBalances: accountResults, totalValues: totalResults, withdrawals: withdrawalResults };
  }

  /**
   * Calculate percentiles for a given period across all simulations
   */
  private calculatePercentiles(values: number[]): SimulationResult['percentiles'] {
    const sorted = [...values].sort((a, b) => a - b);
    const n = sorted.length;

    const percentile = (p: number) => {
      const index = Math.ceil((p / 100) * n) - 1;
      return sorted[Math.max(0, Math.min(index, n - 1))];
    };

    return {
      // p5: percentile(5),
      p10: percentile(10),
      // p25: percentile(25),
      p50: percentile(50),
      // p75: percentile(75),
      // p90: percentile(90),
      // p95: percentile(95),
    };
  }

  /**
   * Run the complete Monte Carlo simulation
   */
  public runSimulation(): SimulationResults {
    const allAccountSimulations: AccountBalance[][][] = [];
    const allTotalSimulations: number[][] = [];
    const allWithdrawalSimulations: number[][] = [];

    console.log('Running Monte Carlo simulation with parameters:', this.parameters);

    const startTime = performance.now();
    // Validate scenario data
    if (!this.scenarioData) {
      throw new Error('Scenario data is required but was not provided.');
    }

    // Enforce simulation constraints
    const maxSimulations = 5000;
    if (this.parameters.numberOfSimulations > maxSimulations) {
      throw new Error('Number of simulations exceeds 5,000. Please reduce the number.');
    }

    const numScenarios = Math.min(this.parameters.numberOfSimulations, maxSimulations);
    const periods = Math.min(this.parameters.simulationPeriods, 35);

    // Run multiple simulations using first N scenario rows
    for (let i = 0; i < numScenarios; i++) {
      const simulation = this.runSingleSimulation(i);
      allAccountSimulations.push(simulation.accountBalances);
      allTotalSimulations.push(simulation.totalValues);
      allWithdrawalSimulations.push(simulation.withdrawals);
    }

    // Calculate percentiles for total portfolio values
    const totalPortfolioResults: ChartDataPoint[] = [];
    for (let period = 0; period < periods; period++) {
      const periodValues = allTotalSimulations.map(simulation => simulation[period]);
      const percentiles = this.calculatePercentiles(periodValues);

      totalPortfolioResults.push({
        period: period + 1,
        ...percentiles
      });
    }

    // Find the simulation runs that match each percentile for account breakdown
    const accountBreakdownResults: AccountBreakdownDataPoint[] = [];
    for (let period = 0; period < periods; period++) {
      const periodTotalValues = allTotalSimulations.map(simulation => simulation[period]);
      const sortedIndices = periodTotalValues
        .map((value, index) => ({ value, index }))
        .sort((a, b) => a.value - b.value)
        .map(item => item.index);

      // const p10Index = sortedIndices[Math.floor(sortedIndices.length * 0.1)];
      const p50Index = sortedIndices[Math.floor(sortedIndices.length * 0.5)];

      // Use p50 for account breakdown (median scenario)
      const medianAccountBalances = allAccountSimulations[p50Index][period];

      const taxDeferred = medianAccountBalances.find(acc => acc.accountType === AccountType.TAX_DEFERRED)?.balance || 0;
      const taxFree = medianAccountBalances.find(acc => acc.accountType === AccountType.TAX_FREE)?.balance || 0;
      const taxableInvestment = medianAccountBalances.find(acc => acc.accountType === AccountType.TAXABLE_INVESTMENT)?.balance || 0;
      const cashSavings = medianAccountBalances.find(acc => acc.accountType === AccountType.CASH_SAVINGS)?.balance || 0;

      accountBreakdownResults.push({
        period: period + 1,
        taxDeferred,
        taxFree,
        taxableInvestment,
        cashSavings,
        total: taxDeferred + taxFree + taxableInvestment + cashSavings
      });
    }

    // Convert all simulation runs to AccountBreakdownDataPoint format
    const allSimulationRuns: AccountBreakdownDataPoint[][] = allAccountSimulations.map(simulation =>
      simulation.map(periodBalances => {
        const taxDeferred = periodBalances.find(acc => acc.accountType === AccountType.TAX_DEFERRED)?.balance || 0;
        const taxFree = periodBalances.find(acc => acc.accountType === AccountType.TAX_FREE)?.balance || 0;
        const taxableInvestment = periodBalances.find(acc => acc.accountType === AccountType.TAXABLE_INVESTMENT)?.balance || 0;
        const cashSavings = periodBalances.find(acc => acc.accountType === AccountType.CASH_SAVINGS)?.balance || 0;

        return {
          period: simulation.indexOf(periodBalances) + 1,
          taxDeferred,
          taxFree,
          taxableInvestment,
          cashSavings,
          total: taxDeferred + taxFree + taxableInvestment + cashSavings
        };
      })
    );

    // Calculate percentiles for withdrawal amounts for each period
    const withdrawalResults: WithdrawalDataPoint[] = [];
    for (let period = 0; period < periods; period++) {
      const periodWithdrawals = allWithdrawalSimulations.map(simulation => simulation[period]);
      const percentiles = this.calculatePercentiles(periodWithdrawals);

      withdrawalResults.push({
        year: period + 1,
        p10: percentiles.p10,
        p50: percentiles.p50,
      });
    }

    const endTime = performance.now();
    const duration = (endTime - startTime) / 1000; // Convert to seconds
    console.log(`Monte Carlo simulation completed in ${duration.toFixed(3)} seconds`);

    return {
      numberOfSimulations: numScenarios,
      simulationPeriods: periods,
      accountBreakdown: accountBreakdownResults,
      totalPortfolioData: totalPortfolioResults,
      withdrawalData: withdrawalResults,
      allSimulationRuns: allSimulationRuns
    };
  }

  /**
   * Validate that allocation percentages sum to 100% for each account
   */
  public static validateAccountBalances(accountBalances: AccountBalance[]): boolean {
    return accountBalances.every(account => {
      const totalPercentage = account.allocations.reduce((sum, alloc) => sum + alloc.percentage, 0);
      return Math.abs(totalPercentage - 100) < 0.01; // Allow for small floating point errors
    });
  }

  /**
   * Validate that allocation percentages sum to 100% (legacy method for backward compatibility)
   */
  public static validateAllocations(allocations: AssetAllocation[]): boolean {
    const totalPercentage = allocations.reduce((sum, alloc) => sum + alloc.percentage, 0);
    return Math.abs(totalPercentage - 100) < 0.01; // Allow for small floating point errors
  }
}
